
import {of as observableOf,  Observable } from 'rxjs';

import {catchError} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { IAddSurveyTemplate, ISurveyTemplateList, ISurveyTemplateResp, IUpdateSurveyTemplate } from '@routes/templates/models/template-model';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/legacy/response';
import { IAllSurveyQuestions, ISurveyQuestion, ISurveyQuestionDisabledInfo, ISurveyQuestionsSettings, ISurveyStatisticsItem,
  IUpdateSurveyQuestionsSetting } from '@shared/services/api/legacy/survey-service/survey-model';

@Injectable()
export class SurveyService {

  constructor(private http: HttpClient) { }

  private static get URL(): string { return `${environment.api.baseUrl}${environment.api.prefix}${SurveyService.ENDPOINT}`; }
  private static get TEMPLATES_URL(): string { return `${environment.api.baseUrl}${environment.api.prefix}${SurveyService.TEMPLATES_ENDPOINT}`; }
  private static readonly ENDPOINT = '/surveyquestions';
  private static readonly TEMPLATES_ENDPOINT = '/surveytemplate';

  public getSurveyQuestionsSettings(): Observable<SuccessResponse<ISurveyQuestionsSettings | IAllSurveyQuestions> | ErrorResponse> {
    return this.http
    .get<SuccessResponse<ISurveyQuestionsSettings | IAllSurveyQuestions>>(SurveyService.URL).pipe(
    catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public getSurveyQuestion(questionId): Observable<SuccessResponse<ISurveyQuestion> | ErrorResponse> {
    return this.http
    .get<SuccessResponse<ISurveyQuestion>>(SurveyService.URL + '/' + questionId).pipe(
    catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public addSurveyQuestion(submitData: IUpdateSurveyQuestionsSetting): Observable<SuccessResponse<boolean> | ErrorResponse> {
    return this.http
    .post<SuccessResponse<boolean>>(SurveyService.URL, submitData).pipe(
    catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public updateSurveyQuestion(submitData: IUpdateSurveyQuestionsSetting): Observable<SuccessResponse<boolean> | ErrorResponse> {
    return this.http
    .put<SuccessResponse<boolean>>(SurveyService.URL, submitData).pipe(
    catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public deleteSurveyQuestion(questionId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
    return this.http
    .delete<SuccessResponse<boolean>>(SurveyService.URL + '/' + questionId).pipe(
    catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public activateSurveyQuestion(questionId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
    return this.http
        .put<SuccessResponse<boolean>>(SurveyService.URL + '/enable/' + questionId, { }).pipe(
        catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public deactivateSurveyQuestion(questionId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
    return this.http
        .put<SuccessResponse<boolean>>(SurveyService.URL + '/disable/' + questionId, { }).pipe(
        catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public addSurveyTemplate(submitData: IAddSurveyTemplate): Observable<SuccessResponse<boolean> | ErrorResponse> {
    return this.http
    .post<SuccessResponse<boolean>>(SurveyService.TEMPLATES_URL, submitData).pipe(
    catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public updateSurveyTemplate(submitData: IUpdateSurveyTemplate): Observable<SuccessResponse<boolean> | ErrorResponse> {
    return this.http
    .put<SuccessResponse<boolean>>(SurveyService.TEMPLATES_URL, submitData).pipe(
    catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public deleteSurveyTemplate(templateId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
    return this.http
    .delete<SuccessResponse<boolean>>(SurveyService.TEMPLATES_URL + '/' + templateId).pipe(
    catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public activateSurveyTemplate(templateId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
    return this.http
        .put<SuccessResponse<boolean>>(SurveyService.TEMPLATES_URL + '/enable/' + templateId, { }).pipe(
        catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public deactivateSurveyTemplate(templateId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
    return this.http
        .put<SuccessResponse<boolean>>(SurveyService.TEMPLATES_URL + '/disable/' + templateId, { }).pipe(
        catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public getSurveyTemplates(): Observable<SuccessResponse<ISurveyTemplateList> | ErrorResponse> {
    return this.http
    .get<SuccessResponse<ISurveyTemplateList>>(SurveyService.TEMPLATES_URL).pipe(
    catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public getSurveyTemplate(surveyTemplateId): Observable<SuccessResponse<ISurveyTemplateResp> | ErrorResponse> {
    return this.http
    .get<SuccessResponse<ISurveyTemplateResp>>(SurveyService.TEMPLATES_URL + '/' + surveyTemplateId).pipe(
    catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public getSurveyQuestionStatistic(surveyQuestionId, data): Observable<SuccessResponse<ISurveyStatisticsItem> | ErrorResponse> {
    return this.http
    .post<SuccessResponse<ISurveyStatisticsItem>>(SurveyService.URL + '/stats/' + surveyQuestionId, data).pipe(
    catchError(error => observableOf(new ErrorResponse(error.error))));
  }

  public getSurveyQuestionDisabledInfo(surveyQuestionId): Observable<SuccessResponse<ISurveyQuestionDisabledInfo> | ErrorResponse> {
    return this.http
    .get<SuccessResponse<ISurveyQuestionDisabledInfo>>(SurveyService.URL + '/disable-info/' + surveyQuestionId).pipe(
    catchError(error => observableOf(new ErrorResponse(error.error))));
  }
}
